// Results since a dispute round: the newest report compared with the report
// the round was DRAFTED FROM (not merely the previous pull). Removed / updated /
// still reporting / newly negative, plus inquiries, public records and scores.
import { useEffect, useState } from "react";
import {
  reports as reportsApi,
  type ComparisonAccount, type ComparisonInquiry, type ComparisonPublicRecord, type ReportComparison,
} from "../lib/api";

function day(iso: string | null | undefined) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}
const BUREAU: Record<string, string> = { equifax: "Equifax", experian: "Experian", transunion: "TransUnion" };
const bureau = (b: string) => BUREAU[b?.toLowerCase()] || b;

const REASON: Record<string, string> = {
  no_round: "No dispute round yet. Once a round is drafted from a report, results show here.",
  no_baseline: "This round isn't linked to the report it was drafted from, so there's nothing to compare against.",
  no_newer_report: "No report has been pulled since this round was drafted. Use Pull now (Analysis tab) once the bureaus have had time to respond.",
  current_incomplete: "The newest report didn't come through complete, so it can't be compared safely. Pull a fresh report (Analysis tab) and check again.",
};

function warningText(w: string): string | null {
  if (w === "current_truncated") return "The newest report was too large to save in full, so it can't be compared safely. Pull a fresh report.";
  if (w === "baseline_truncated") return "The round's original report was too large to save in full, so nothing is labeled as new.";
  if (w === "inquiries_unavailable") return "This report only lists inquiry counts, not the inquiries themselves, so inquiry results aren't shown.";
  const amb = /^ambiguous_accounts:(\w+):(\d+)$/.exec(w);
  if (amb) return `${amb[2]} ${bureau(amb[1])} account${amb[2] === "1" ? "" : "s"} share a lender name without account numbers, so they're left out instead of guessed.`;
  const m = /^(current|baseline)_missing_bureau:(\w+)$/.exec(w);
  if (m) return m[1] === "current"
    ? `The newest report has no ${bureau(m[2])} data, so ${bureau(m[2])} results aren't shown yet.`
    : `The round's original report had no ${bureau(m[2])} data.`;
  const inc = /^(current|baseline)_incomplete_bureau:(\w+)$/.exec(w);
  if (inc) return inc[1] === "current"
    ? `The newest report didn't load every ${bureau(inc[2])} account, so ${bureau(inc[2])} removals aren't claimed yet. Pull a fresh report to confirm.`
    : `The round's original report didn't load every ${bureau(inc[2])} account, so new ${bureau(inc[2])} items may not really be new.`;
  return null;
}

function AccountList({ items, tone, empty }: { items: ComparisonAccount[]; tone: "good" | "info" | "bad"; empty: string }) {
  if (!items.length) return <p className="muted small" style={{ margin: "4px 0 0" }}>{empty}</p>;
  const color = tone === "good" ? "var(--ne-success-text)" : tone === "bad" ? "var(--ne-red-dark)" : "var(--ne-text-2)";
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: "6px 0 0", display: "grid", gap: 6 }}>
      {items.map((a, i) => (
        <li key={`${a.bureau}-${a.creditor_name}-${a.account_number_last4}-${i}`}
          style={{ border: "1px solid var(--ne-line)", borderRadius: 8, padding: "8px 10px", background: "var(--ne-surface-2)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
            <strong style={{ color }}>{a.creditor_name}{a.account_number_last4 ? ` …${a.account_number_last4}` : ""}</strong>
            <span className="muted small">{bureau(a.bureau)}</span>
          </div>
          {a.changes.length > 0
            ? <div className="muted small" style={{ marginTop: 2 }}>{a.changes.join(" · ")}</div>
            : (a.after_status || a.before_status) && <div className="muted small" style={{ marginTop: 2 }}>{a.after_status || a.before_status}</div>}
        </li>
      ))}
    </ul>
  );
}

function SmallList<T>({ items, render, empty }: { items: T[]; render: (t: T) => string; empty: string }) {
  if (!items.length) return <span className="muted small">{empty}</span>;
  return <span className="small">{items.map(render).join(" · ")}</span>;
}

export default function ReportComparisonPanel({ clientId, rounds, refreshKey }: {
  clientId: string;
  rounds: Array<{ id: string; round_number: number }>;
  refreshKey?: string | number;
}) {
  const [roundId, setRoundId] = useState<string>("");
  const [data, setData] = useState<ReportComparison | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr(null);
    reportsApi.comparison(clientId, roundId || undefined)
      .then((d) => { if (!cancelled) { setData(d); if (!roundId && d.round) setRoundId(d.round.id); } })
      .catch((e) => { if (!cancelled) setErr(e?.message || "Could not load the comparison."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId, roundId, refreshKey]);

  const removedCount = data ? data.accounts.removed.length + data.inquiries.removed.length + data.public_records.removed.length : 0;

  return (
    <section className="sub-panel" aria-labelledby="report-comparison-title" data-testid="report-comparison" style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h3 id="report-comparison-title" style={{ margin: "0 0 4px" }}>Results since the dispute round</h3>
          <p className="muted small" style={{ margin: 0 }}>
            Compares the newest report with the report the round was drafted from.
            {data?.baseline && data?.current && <> Drafted from the {day(data.baseline.imported_at)} report · compared with {day(data.current.imported_at)}.</>}
          </p>
        </div>
        {rounds.length > 1 && (
          <label className="form-field" style={{ margin: 0, minWidth: 160 }}>
            <span>Round</span>
            <select value={roundId} onChange={(e) => setRoundId(e.target.value)} data-testid="comparison-round">
              {[...rounds].sort((a, b) => b.round_number - a.round_number).map((r) => (
                <option key={r.id} value={r.id}>Round {r.round_number}</option>
              ))}
            </select>
          </label>
        )}
      </div>

      {loading && <p className="muted small" style={{ marginTop: 10 }}>Comparing reports…</p>}
      {!loading && err && <p className="error-text" role="alert" style={{ marginTop: 10 }}>{err}</p>}
      {!loading && !err && data && data.reason && <p className="muted small" style={{ marginTop: 10 }}>{REASON[data.reason]}</p>}

      {!loading && !err && data && !data.reason && (
        <div style={{ display: "grid", gap: 14, marginTop: 12 }}>
          {(data.warnings || []).map(warningText).filter(Boolean).map((t) => (
            <p key={t} className="muted small" role="note" style={{ margin: 0, color: "var(--ne-warning-text)" }}><strong>Heads up:</strong> {t}</p>
          ))}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <span className="status-pill" style={{ background: "var(--ne-success-bg)", color: "var(--ne-success-text)", border: "1px solid var(--ne-success-border)" }}>
              {removedCount} removed
            </span>
            <span className="status-pill">{data.accounts.updated.length} updated</span>
            <span className="status-pill">{data.accounts.still_reporting.length} still reporting</span>
            {data.accounts.new_negative.length > 0 && (
              <span className="status-pill" style={{ background: "var(--ne-red-soft)", color: "var(--ne-red-dark)" }}>{data.accounts.new_negative.length} new negative</span>
            )}
          </div>

          {data.scores && (
            <div className="small" style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
              {(["equifax", "experian", "transunion"] as const).map((b) => {
                const s = data.scores?.[b];
                if (!s) return null;
                const d = s.delta;
                return (
                  <span key={b}>
                    <strong>{bureau(b)}</strong> {s.before ?? "—"} → {s.after ?? "—"}{" "}
                    {d != null && d !== 0 && <span style={{ color: d > 0 ? "var(--ne-success-text)" : "var(--ne-red-dark)" }}>({d > 0 ? "+" : ""}{d})</span>}
                  </span>
                );
              })}
            </div>
          )}

          <div><p className="field-label" style={{ margin: 0 }}>Removed</p>
            <AccountList items={data.accounts.removed} tone="good" empty="No disputed accounts removed yet." /></div>
          <div><p className="field-label" style={{ margin: 0 }}>Updated</p>
            <AccountList items={data.accounts.updated} tone="info" empty="No changes on the disputed accounts." /></div>
          <div><p className="field-label" style={{ margin: 0 }}>Still reporting</p>
            <AccountList items={data.accounts.still_reporting} tone="bad" empty="Nothing negative is still reporting unchanged." /></div>
          {data.accounts.new_negative.length > 0 && (
            <div><p className="field-label" style={{ margin: 0 }}>New negative items</p>
              <AccountList items={data.accounts.new_negative} tone="bad" empty="" /></div>
          )}

          <div className="small" style={{ display: "grid", gap: 4 }}>
            <div><strong>Inquiries removed:</strong>{" "}
              <SmallList<ComparisonInquiry> items={data.inquiries.removed} empty="none"
                render={(q) => `${q.creditor_name} (${bureau(q.bureau)}${q.inquiry_date ? `, ${q.inquiry_date}` : ""})`} /></div>
            <div><strong>Inquiries still reporting:</strong> {data.inquiries.still_reporting.length}
              {data.inquiries.new.length > 0 && <> · <strong>new:</strong> {data.inquiries.new.length}</>}</div>
            <div><strong>Public records removed:</strong>{" "}
              <SmallList<ComparisonPublicRecord> items={data.public_records.removed} empty="none"
                render={(p) => `${p.kind}${p.case_number ? ` ${p.case_number}` : ""} (${bureau(p.bureau)})`} /></div>
            {data.public_records.still_reporting.length > 0 && (
              <div><strong>Public records still reporting:</strong> {data.public_records.still_reporting.length}</div>
            )}
            {data.public_records.new.length > 0 && (
              <div style={{ color: "var(--ne-red-dark)" }}><strong>New public records:</strong>{" "}
                {data.public_records.new.map((p) => `${p.kind}${p.case_number ? ` ${p.case_number}` : ""} (${bureau(p.bureau)}${p.filing_date ? `, filed ${p.filing_date}` : ""})`).join(" · ")}</div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
